import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { utcMoment } from '@vectronic/util';
import { ScheduleCalendarCalculationService } from '../schedule-calendar-calculation.service';
import { GPS_SCHEDULE_CONFIG, GpsScheduleService } from '../schedule.service';
import { RemoteCommandsService } from './remote-commands.service';

@Injectable({ providedIn: 'root' })
export class ScheduleConstraintErrorsService {
  private remoteCommandsService = inject(RemoteCommandsService);
  private gpsScheduleService = inject(GpsScheduleService);
  private calendarService = inject(ScheduleCalendarCalculationService);

  private fixPerTransmission = toSignal(this.gpsScheduleService.fixPerTransmission$);
  private gpsRules = toSignal(this.gpsScheduleService.rules$);

  setDeviceContext = signal<
    { deviceTypeCustomerName?: string; comType?: string } | null | undefined
  >(null);

  private isDeviceVertexMiniGlobalstar(device: {
    deviceTypeCustomerName?: string;
    comType?: string;
  }): boolean {
    if (!device) return false;
    const name = (device.deviceTypeCustomerName ?? '').toLowerCase();
    return name.includes('vertex mini') && device.comType === 'GLOBALSTAR';
  }

  vertexMiniGlobalstarError = computed((): string | null => {
    const directContext = this.setDeviceContext();
    const isVertexMini = directContext
      ? this.isDeviceVertexMiniGlobalstar(directContext)
      : this.remoteCommandsService.isVertexMiniGlobalstar();

    if (!isVertexMini) return null;

    const rules = this.gpsRules() ?? {};
    const fpt = this.fixPerTransmission() ?? {};

    const validEntries = Object.entries(rules).filter(
      ([, rule]) => rule?.periodLength && rule.startTime && rule.endTime,
    );

    if (!validEntries.length) return null;

    const minStart = validEntries.reduce((min, [, rule]) => {
      const t = utcMoment(rule.startTime).startOf('day');
      return t.isBefore(min) ? t : min;
    }, utcMoment(validEntries[0][1].startTime).startOf('day'));

    const maxEnd = validEntries.reduce((max, [, rule]) => {
      const t = utcMoment(rule.endTime).endOf('day');
      return t.isAfter(max) ? t : max;
    }, utcMoment(validEntries[0][1].endTime).endOf('day'));

    // Walk day by day and count combined transmitted fixes from all rules
    const MAX_DAYS = 50 * 365; // guard against infinite loop
    let daysIterated = 0;
    const cursor = minStart.clone();
    while (cursor.isSameOrBefore(maxEnd, 'day') && daysIterated++ < MAX_DAYS) {
      const { start, end } = this.calendarService.getRange(cursor, 'day');

      const combinedTransmitted = validEntries.reduce((total, [indexStr, rule]) => {
        const index = parseInt(indexStr);
        const schedules = this.calendarService.schedulesInRange(
          rule,
          start,
          end,
          index,
          GPS_SCHEDULE_CONFIG,
          fpt[index],
        );
        return total + schedules.filter((s) => s.isTransmitted).length;
      }, 0);

      if (combinedTransmitted > 1) {
        return 'Vertex Mini Globalstar only allows 1 transmitted fix per day';
      }

      cursor.add(1, 'day');
    }

    return null;
  });
}
